import { Trans } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { Translate } from "@phosphor-icons/react";
import { useBreakpoint } from "@stylingcv/hooks";
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@stylingcv/ui";
import { useState } from "react";

import { changeLanguage } from "../providers/locale";
import { LocaleCombobox } from "./locale-combobox";

export const LocaleSwitch = ({ className }: { className?: string }) => {
  const { i18n } = useLingui();
  const [open, setOpen] = useState(false);
  const { isMobile } = useBreakpoint();

  const languages = [
    { code: "en-US", name: "English", flag: "https://flagcdn.com/w40/gb.png" },
    { code: "ar-SA", name: "العربية", flag: "https://flagcdn.com/w40/sa.png" },
    { code: "fr-FR", name: "Français", flag: "https://flagcdn.com/w40/fr.png" },
    { code: "de-DE", name: "Deutsch", flag: "https://flagcdn.com/w40/de.png" },
    { code: "es-ES", name: "Español", flag: "https://flagcdn.com/w40/es.png" },
    { code: "it-IT", name: "Italiano", flag: "https://flagcdn.com/w40/it.png" },
    { code: "pt-PT", name: "Português", flag: "https://flagcdn.com/w40/pt.png" },
    { code: "nl-NL", name: "Nederlands", flag: "https://flagcdn.com/w40/nl.png" },
  ];

  const currentLang = languages.find((l) => l.code === i18n.locale) || languages.find((l) => l.code === "en-US") || languages[0];

  // Mobile version using Sheet
  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <Button size="icon" variant="ghost" aria-label="Change language" className={`flex items-center justify-center p-0 overflow-hidden ${className || "size-8 rounded-xl border border-white/10 bg-white/[0.03] text-slate-600 transition-colors hover:bg-white/[0.08] hover:text-cyan-400 dark:text-slate-300"}`}>
            <img src={currentLang.flag} alt={currentLang.name} className="size-5 object-cover rounded-full" />
          </Button>
        </SheetTrigger>
        <SheetContent side="bottom" className="mobile-language-sheet max-h-[80vh] rounded-t-xl">
          <SheetHeader className="pb-4 text-center">
            <SheetTitle>
              <Trans>
                <Trans>Select Language</Trans>
              </Trans>
            </SheetTitle>
          </SheetHeader>
          <div className="px-4 pb-6">
            <LocaleCombobox
              value={i18n.locale}
              onValueChange={async (locale) => {
                await changeLanguage(locale);
                setOpen(false);
              }}
            />
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  // Desktop version using Popover
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="icon" variant="ghost" aria-label="Change language" className={`flex items-center justify-center p-0 overflow-hidden ${className || "size-8 rounded-xl border border-white/10 bg-white/[0.03] text-slate-600 transition-colors hover:bg-white/[0.08] hover:text-cyan-400 dark:text-slate-300"}`}>
          <img src={currentLang.flag} alt={currentLang.name} className="size-5 object-cover rounded-full" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="p-0 z-[10000]">
        <LocaleCombobox
          value={i18n.locale}
          onValueChange={async (locale) => {
            await changeLanguage(locale);
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
};
